/**
 * Skill files — markdown with YAML frontmatter in the open Agent Skills format
 * (agentskills.io/specification): a required `name` (the skill's own name) and
 * a required `description` (what it does and when to use it — the skills menu
 * shows it to the agent). One reader for those fields, and the rule every save
 * follows (dashboard, agent tool, the bundled-skill seeder).
 */

/** Spec limits (agentskills.io/specification). */
const MAX_NAME = 64;
const MAX_DESCRIPTION = 1024;

/** The frontmatter block (without its `---` fences), or null when the file has none. */
function frontmatter(content: string): string | null {
  if (!content.startsWith('---')) return null;
  const endIdx = content.indexOf('\n---', 3);
  return endIdx === -1 ? null : content.slice(3, endIdx);
}

/** The single-line fields read here (no YAML parser). */
const FIELD = { name: /^name:\s*(.+)$/m, description: /^description:\s*(.+)$/m } as const;

/** One top-level single-line frontmatter field, quotes removed. */
function field(content: string, key: keyof typeof FIELD): string | null {
  const block = frontmatter(content);
  if (block === null) return null;
  const match = block.match(FIELD[key]);
  if (!match?.[1]) return null;
  let value = match[1].trim();
  // Strip one matching pair of surrounding quotes, or a quoted value would
  // show its literal quotes in the skills menu.
  const first = value[0];
  if (value.length >= 2 && (first === '"' || first === "'") && value.endsWith(first)) {
    value = value.slice(1, -1).trim();
  }
  return value || null;
}

/** The skill's `description:` — what it does and when to use it. */
export function skillDescription(content: string): string | null {
  return field(content, 'description');
}

/** The skill's `name:` as written in its frontmatter. */
export function skillFrontmatterName(content: string): string | null {
  return field(content, 'name');
}

/** Whether `name` is lowercase kebab-case (e.g. `react-architecture`): words of
 *  a–z / 0–9 joined by single dashes. */
function isKebabCase(name: string): boolean {
  return /^[a-z0-9-]+$/.test(name) && !name.startsWith('-') && !name.endsWith('-') && !name.includes('--');
}

/** Why `name` can't name a skill (its name is also its file name), or null when it can. */
export function skillNameProblem(name: string): string | null {
  if (!isKebabCase(name)) return `"${name}" isn't a skill name: use lowercase kebab-case, e.g. react-architecture`;
  if (name.length > MAX_NAME) return `"${name}" is too long for a skill name (at most ${MAX_NAME} characters)`;
  return null;
}

/**
 * Why `content` can't be saved as a skill, or null when it can. With `name`,
 * the frontmatter's `name:` must equal it — the spec ties a skill's declared
 * name to where it is stored.
 */
export function skillProblem(content: string, name?: string): string | null {
  if (content.trim() === '') return 'a skill needs content';
  const description = skillDescription(content);
  if (description === null) {
    return 'a skill needs a description: start the file with frontmatter `---\\nname: <name>\\ndescription: <what it does and when to use it>\\n---`';
  }
  if (description.length > MAX_DESCRIPTION) {
    return `a skill's description is at most ${MAX_DESCRIPTION} characters (this one is ${description.length})`;
  }
  const declared = skillFrontmatterName(content);
  if (declared === null) return 'a skill needs a `name:` in its frontmatter (the skill’s own name)';
  if (name !== undefined && declared !== name) {
    return `the frontmatter says \`name: ${declared}\` but the skill is "${name}" — they must match`;
  }
  return null;
}

/** The file a new skill starts as: its name and description in frontmatter, then a heading. */
export function newSkillFile(name: string, description: string): string {
  return `---\nname: ${name}\ndescription: ${description.trim()}\n---\n\n# ${name}\n`;
}
